import { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { getInflation } from '../services/api';

export default function InflationPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getInflation()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="muted">Loading…</p>;

  const firstPrice = data.length > 0 ? Number(data[0].avg_price_per_unit) : null;
  const lastPrice = data.length > 0 ? Number(data[data.length - 1].avg_price_per_unit) : null;
  const totalChange = firstPrice && lastPrice
    ? ((lastPrice - firstPrice) / firstPrice * 100).toFixed(1)
    : null;

  return (
    <div>
      <h2 style={{ marginBottom: '0.25rem' }}>Personal basket inflation</h2>
      <p className="muted" style={{ marginBottom: '1.5rem' }}>
        Average price per canonical unit across all categories, month by month
      </p>

      {data.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)' }}>
          No data yet — upload some receipts to see your inflation trend.
        </div>
      )}

      {data.length > 0 && (
        <>
          <div className="stat-row">
            <div className="card">
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Earliest avg price</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{Number(firstPrice).toFixed(2)} kr</div>
              <div className="muted" style={{ fontSize: 12 }}>per canonical unit — {data[0]?.month}</div>
            </div>
            <div className="card">
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Latest avg price</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{Number(lastPrice).toFixed(2)} kr</div>
              <div className="muted" style={{ fontSize: 12 }}>per canonical unit — {data[data.length - 1]?.month}</div>
            </div>
            <div className="card">
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Total change</div>
              <div style={{
                fontSize: 22,
                fontWeight: 700,
                color: Number(totalChange) > 0 ? 'var(--color-danger)' : 'var(--color-success)',
              }}>
                {Number(totalChange) > 0 ? '+' : ''}{totalChange}%
              </div>
              <div className="muted" style={{ fontSize: 12 }}>over {data.length} months</div>
            </div>
          </div>

          <div className="card">
            <ResponsiveContainer width="100%" height={360}>
              <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-chart-accent)" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="var(--color-chart-accent)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-chart-grid)" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--color-chart-axis)' }} />
                <YAxis tick={{ fontSize: 12, fill: 'var(--color-chart-axis)' }} unit=" kr" domain={['auto', 'auto']} />
                <Tooltip
                  formatter={(value) => [`${Number(value).toFixed(2)} kr`, 'Avg price/unit']}
                  contentStyle={{
                    fontSize: 13,
                    borderRadius: 6,
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text)',
                  }}
                  labelStyle={{ color: 'var(--color-text-muted)' }}
                  itemStyle={{ color: 'var(--color-text)' }}
                />
                <Area
                  type="monotone"
                  dataKey="avg_price_per_unit"
                  stroke="var(--color-chart-accent)"
                  strokeWidth={2}
                  fill="url(#priceGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="card" style={{ marginTop: '1rem', padding: 0 }}>
            <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Month</th>
                  <th style={{ textAlign: 'right' }}>Avg price/unit</th>
                  <th style={{ textAlign: 'right' }}>Observations</th>
                  <th style={{ textAlign: 'right' }}>MoM change</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => {
                  const prev = i > 0 ? Number(data[i - 1].avg_price_per_unit) : null;
                  const curr = Number(row.avg_price_per_unit);
                  const change = prev ? ((curr - prev) / prev * 100) : null;
                  return (
                    <tr key={row.month}>
                      <td>{row.month}</td>
                      <td style={{ textAlign: 'right' }}>{curr.toFixed(2)} kr</td>
                      <td style={{ textAlign: 'right', color: 'var(--color-text-muted)' }}>{row.observation_count}</td>
                      <td style={{
                        textAlign: 'right',
                        color: change === null ? 'inherit'
                          : change > 0 ? 'var(--color-danger)'
                          : 'var(--color-success)',
                      }}>
                        {change === null ? '—' : `${change > 0 ? '+' : ''}${change.toFixed(2)}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
